import * as THREE from 'three'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import React, { useRef, useMemo } from 'react'

function TextSprite({ text, color = '#fff', size = 128, scale = 0.28, position = [0, 0, 0] as any }: { text: string; color?: string; size?: number; scale?: number; position?: [number, number, number] }) {
  const map = useMemo(() => {
    const canvas = document.createElement('canvas')
    const ctxSize = size
    canvas.width = ctxSize
    canvas.height = ctxSize
    const ctx = canvas.getContext('2d')!
    // transparent background
    ctx.clearRect(0, 0, ctxSize, ctxSize)
    // text styling
    ctx.fillStyle = color
    const fontSize = Math.floor(ctxSize * 0.6)
    ctx.font = `bold ${fontSize}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, ctxSize / 2, ctxSize / 2)
    const tex = new THREE.CanvasTexture(canvas)
    tex.needsUpdate = true
    return tex
  }, [text, color, size])

  return (
    <sprite position={position} scale={[scale, scale, 1]}>
      <spriteMaterial map={map} transparent depthTest={false} depthWrite={false} />
    </sprite>
  )
}

function GizmoAxes() {
  // axesHelper for visual axes
  const letter_d = 0.7;
  return (
    <group>
      <axesHelper args={[0.6]} />
      {/* Labels positioned slightly beyond the axes ends */}
      <TextSprite text="X" color="#ff4444" position={[letter_d, 0, 0]} scale={0.28} />
      {/* <TextSprite text="-X" color="#aa2222" position={[-letter_d, 0, 0]} scale={0.22} /> */}
      <TextSprite text="Y" color="#44ff44" position={[0, letter_d, 0]} scale={0.28} />
      {/* <TextSprite text="-Y" color="#228822" position={[0, -letter_d, 0]} scale={0.22} /> */}
      <TextSprite text="Z" color="#4444ff" position={[0, 0, letter_d]} scale={0.28} />
      {/* <TextSprite text="-Z" color="#222288" position={[0, 0, -letter_d]} scale={0.22} /> */}
    </group>
  )
}

function GizmoController({ mainCameraRef }: { mainCameraRef?: React.RefObject<any> }) {
  const ref = useRef<THREE.Group>(null)
  const { camera } = useThree()

  useFrame(() => {
    if (!ref.current) return

    // Prefer the main scene camera (passed from parent) so the gizmo matches the real view
    const srcCamera = mainCameraRef?.current?.object ?? camera
    if (!srcCamera || !srcCamera.quaternion) return

    // Copy inverse camera rotation (editor behavior)
    ref.current.quaternion.copy(srcCamera.quaternion).invert()
  })

  return (
    <group ref={ref}>
      <GizmoAxes />
    </group>
  )
}

export function ViewportGizmo({ mainCameraRef }: { mainCameraRef?: React.RefObject<any> }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 1,
        left: 1,
        width: 120,
        height: 120,
        pointerEvents: 'none' // does not block scene interaction
      }}
    >
      <Canvas
        orthographic
        camera={{ zoom: 80, position: [0, 0, 10] }}
      >
        <ambientLight intensity={1} />
        <GizmoController mainCameraRef={mainCameraRef} />
      </Canvas>
    </div>
  )
}
